/**
 * `ThreeDCanvasSettings` — GVW-151 per-session 3D-dark-canvas toggle.
 *
 * Per brief §2 / §7: opt-in neutral-dark 3D viewport. Phase-2 STRETCH; the
 * `--canvas-3d-neutral` token is still a proposal per boss §14.2, so this
 * toggle is rendered as a non-functional visual variant per §7 / dispatch
 * §4.4.c. The `setThreeDCanvasTone` store action is wired (so when the
 * token lands, removing the `disabled` flag is a one-line change).
 *
 * Persistence: per-session via `useViewerStore` per dispatch §1 (no
 * localStorage; brief §13 says "per-session setting (no persistence)").
 *
 * Cite: design/30-engineering/ui-implementation/GVW-151/brief.md
 */

import { useEffect } from 'react';
import { tokens } from '@/design';
import { useViewerStore } from '@/state/useViewerStore';
import { useLiveRegion } from '@/state/liveRegion';

// Brief §16 token gap: `--canvas-3d-neutral` not yet approved. Toggle
// stays disabled per brief §7 disabled state and the helper text is the
// `gvw.151.disabled.body` copy key.
const TOKEN_GAP_LOCKED = true;

export function ThreeDCanvasSettings() {
  const liveRegion = useLiveRegion();
  const tone = useViewerStore((s) => s.threeDCanvasTone);
  const setTone = useViewerStore((s) => s.setThreeDCanvasTone);

  useEffect(() => {
    document.title = '3D canvas tone · GeoViewer';
    liveRegion.announce('status', '3D canvas tone settings loaded.');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="gvw-151-heading"
      data-gvw-id="GVW-151"
      data-route="/workspace/settings/3d-canvas"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-151-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          dark_mode
        </span>
        3D canvas tone
      </h1>

      <p style={{ marginTop: 0, marginBottom: tokens.space[5], color: tokens.color.ink.secondary, fontSize: tokens.font.size.md }}>
        Choose the background tone for 3D viewports. Only affects 3D-dominant scenes; 2D map, profile, and section canvases stay light.
      </p>

      <div
        role="status"
        style={{
          padding: tokens.space[3],
          marginBottom: tokens.space[5],
          background: tokens.color.surface[1],
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.warning}`,
          borderRadius: tokens.radius.md,
          fontSize: tokens.font.size.sm,
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, marginRight: tokens.space[2], verticalAlign: 'middle' }}>
          info
        </span>
        This setting is not available in this release. The neutral-dark canvas tone token is a Phase-2 proposal.
      </div>

      <fieldset
        disabled={TOKEN_GAP_LOCKED}
        style={{ border: 'none', padding: 0, margin: 0, maxWidth: 560 }}
      >
        <legend style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.medium, marginBottom: tokens.space[2] }}>
          Canvas appearance
        </legend>
        <div role="radiogroup" aria-label="3D canvas appearance" style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2] }}>
          <label
            style={{
              display: 'flex',
              gap: tokens.space[2],
              alignItems: 'center',
              padding: tokens.space[3],
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              background: tone === 'light' ? tokens.color.surface[2] : tokens.color.surface[1],
              opacity: TOKEN_GAP_LOCKED ? 0.6 : 1,
            }}
          >
            <input
              type="radio"
              name="tone"
              value="light"
              checked={tone === 'light'}
              onChange={() => setTone('light')}
              disabled={TOKEN_GAP_LOCKED}
            />
            <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.ink.secondary }}>
              light_mode
            </span>
            <div>
              <div style={{ fontWeight: tokens.font.weight.medium }}>Light (default)</div>
              <div style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>Standard light background</div>
            </div>
          </label>
          <label
            id="gvw-151-disabled-body"
            style={{
              display: 'flex',
              gap: tokens.space[2],
              alignItems: 'center',
              padding: tokens.space[3],
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              background: tone === 'neutral-dark' ? tokens.color.surface[2] : tokens.color.surface[1],
              opacity: TOKEN_GAP_LOCKED ? 0.6 : 1,
            }}
          >
            <input
              type="radio"
              name="tone"
              value="neutral-dark"
              checked={tone === 'neutral-dark'}
              onChange={() => setTone('neutral-dark')}
              disabled={TOKEN_GAP_LOCKED}
              aria-describedby="gvw-151-disabled-help"
            />
            <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 18, color: tokens.color.ink.secondary }}>
              dark_mode
            </span>
            <div>
              <div style={{ fontWeight: tokens.font.weight.medium }}>Neutral-dark</div>
              <div id="gvw-151-disabled-help" style={{ fontSize: tokens.font.size.sm, color: tokens.color.ink.secondary }}>
                Dark neutral background for 3D-dominant scenes
              </div>
            </div>
          </label>
        </div>
      </fieldset>

      <p style={{ marginTop: tokens.space[5], color: tokens.color.ink.muted, fontSize: tokens.font.size.sm, fontStyle: 'italic' }}>
        Per-session setting; resets on reload.
      </p>
    </main>
  );
}
